import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileUp, Plus, Search } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { downloadCSV, todayISO, WEEKDAYS } from '@/lib/hr'
import { staffRoleLabel } from '@/lib/staff-roles'
import { cn } from '@/lib/utils'
import type { HRCandidate, HREmployee, HRSettings } from '@/types/hr'
import { Empty, Field, NativeSelect, Segmented, errMsg } from './shared'
import { EMPTY_PERSON, PersonForm, personPayload, type PersonFormValue } from './PersonForm'
import { PersonSheet } from './PersonSheet'
import { ImportDialog } from './ImportDialog'
import { hrRole } from './HRPage'

// People on the payroll (F-HR-N1): everyone the business pays, with or
// without a POS login. Pay columns show for the owner and the accountant only.

export function useHRSettings() {
  const q = useQuery({ queryKey: ['hr', 'settings'], queryFn: () => apiClient.hrGetSettings(), staleTime: 60_000 })
  return q.data?.data as HRSettings | undefined
}

export function useHREmployees(status: 'active' | 'left' | 'all' = 'active') {
  const q = useQuery({ queryKey: ['hr', 'employees', status], queryFn: () => apiClient.hrListEmployees(status) })
  return { people: (q.data?.data as HREmployee[] | undefined) ?? [], isLoading: q.isLoading }
}

export function payLabel(e: Pick<HREmployee, 'pay_type' | 'current_rate'>, money: (n: number) => string): string {
  if (e.current_rate == null) return 'Not set'
  return `${money(e.current_rate)} / ${e.pay_type === 'daily' ? 'day' : 'month'}`
}

export function PeopleTab({ personId, onPerson }: { personId?: string; onPerson: (id: string | undefined) => void }) {
  const { formatCurrency } = useCurrency()
  const { isAdmin, seesPay, canMark } = hrRole()
  const [status, setStatus] = useState<'active' | 'left'>('active')
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const { people, isLoading } = useHREmployees(status)
  const settings = useHRSettings()

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? people.filter((p) => [p.full_name, p.designation, p.department, p.phone].some((x) => x?.toLowerCase().includes(s))) : people
  }, [people, q])

  const exportCSV = () => {
    const header = ['Name', 'Job', 'Department', 'Joined', 'Left', 'Weekly off', 'Phone', 'Login']
    if (seesPay) header.push('Pay type', 'Pay', 'Salary owed', 'Advance owed', 'Paid by', 'Bank', 'Account')
    const rows = shown.map((p) => {
      const r: unknown[] = [p.full_name, p.designation ?? '', p.department, p.join_date, p.exit_date ?? '', p.weekly_off_days.map((d) => WEEKDAYS[d]).join(' '), p.phone ?? '', p.username ?? '']
      if (seesPay) r.push(p.pay_type, p.current_rate ?? '', p.salary_due ?? 0, p.advance_outstanding ?? 0, p.payout_method ?? '', p.bank_name ?? '', p.account_number ?? '')
      return r
    })
    downloadCSV(`staff-${todayISO()}.csv`, [header, ...rows])
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { id: 'active', label: 'Working' },
              { id: 'left', label: 'Left' },
            ]}
          />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, job, phone" className="h-9 w-56 pl-8" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!shown.length}>
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setImporting(true)}>
              <FileUp className="mr-1.5 h-4 w-4" />
              Import from Excel
            </Button>
          )}
          {canMark && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add person
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : shown.length === 0 ? (
        <Empty>
          {status === 'left' ? 'Nobody has left.' : 'No one here yet. Add people one by one, or import your Excel sheet.'}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Department</th>
                <th className="px-4 py-2 font-medium">Joined</th>
                <th className="px-4 py-2 font-medium">Off</th>
                <th className="px-4 py-2 font-medium">POS login</th>
                {seesPay && (
                  <>
                    <th className="px-4 py-2 text-right font-medium">Pay</th>
                    <th className="px-4 py-2 text-right font-medium">Owed to them</th>
                    <th className="px-4 py-2 text-right font-medium">Advance owed</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {shown.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onPerson(p.id)}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{p.full_name}</div>
                    {p.designation && <div className="text-xs text-muted-foreground">{p.designation}</div>}
                  </td>
                  <td className="px-4 py-2.5">{p.department}</td>
                  <td className="px-4 py-2.5 tabular-nums">{p.join_date}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{p.weekly_off_days.map((d) => WEEKDAYS[d]).join(', ') || '—'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {p.username ? `${p.username} · ${staffRoleLabel(p.login_role ?? '')}` : 'No login'}
                  </td>
                  {seesPay && (
                    <>
                      <td className={cn('px-4 py-2.5 text-right tabular-nums', p.current_rate == null && 'text-amber-700 dark:text-amber-300')}>
                        {payLabel(p, formatCurrency)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(Math.max(0, p.salary_due ?? 0))}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.advance_outstanding ? formatCurrency(p.advance_outstanding) : '—'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddPersonDialog open={adding} onClose={() => setAdding(false)} settings={settings} onAdded={(id) => onPerson(id)} />
      {isAdmin && <ImportDialog open={importing} onClose={() => setImporting(false)} settings={settings} />}
      <PersonSheet id={personId} onClose={() => onPerson(undefined)} settings={settings} />
    </div>
  )
}

function AddPersonDialog({
  open,
  onClose,
  settings,
  onAdded,
}: {
  open: boolean
  onClose: () => void
  settings?: HRSettings
  onAdded: (id: string) => void
}) {
  const qc = useQueryClient()
  const { isAdmin } = hrRole()
  const [value, setValue] = useState<PersonFormValue>({ ...EMPTY_PERSON, join_date: todayISO() })
  const [from, setFrom] = useState('')
  const candQ = useQuery({ queryKey: ['hr', 'candidates'], queryFn: () => apiClient.hrCandidates(), enabled: open })
  const candidates = (candQ.data?.data as HRCandidate[] | undefined) ?? []

  const pick = (key: string) => {
    setFrom(key)
    const c = candidates.find((x) => (x.user_id ?? x.house_account_id) === key)
    if (c) setValue((v) => ({ ...v, full_name: c.name, phone: c.phone ?? v.phone }))
  }
  const add = useMutation({
    mutationFn: () => {
      const c = candidates.find((x) => (x.user_id ?? x.house_account_id) === from)
      return apiClient.hrCreateEmployee({
        ...personPayload(value, { admin: isAdmin, withRate: true }),
        user_id: c?.user_id,
        house_account_id: c?.house_account_id,
      })
    },
    onSuccess: (res) => {
      toastHelpers.success('Added', value.full_name)
      qc.invalidateQueries({ queryKey: ['hr'] })
      onClose()
      setValue({ ...EMPTY_PERSON, join_date: todayISO() })
      setFrom('')
      if (res.data?.id) onAdded(res.data.id)
    },
    onError: (err) => toastHelpers.error('Couldn’t add', errMsg(err)),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a person</DialogTitle>
          <DialogDescription>Anyone you pay — with a POS login or without one (cleaners, helpers, riders).</DialogDescription>
        </DialogHeader>
        {candidates.length > 0 && (
          <Field label="Already in the POS?" hint="Pick a staff login or a meal-allowance holder to link them">
            <NativeSelect value={from} onChange={(e) => pick(e.target.value)}>
              <option value="">No — someone new</option>
              {candidates.map((c) => (
                <option key={c.user_id ?? c.house_account_id} value={c.user_id ?? c.house_account_id}>
                  {c.name} · {c.kind === 'login' ? `login (${staffRoleLabel(c.role ?? '')})` : 'meal allowance, no login'}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}
        <PersonForm value={value} onChange={setValue} departments={settings?.departments ?? ['Other']} admin={isAdmin} showRate />
        {!isAdmin && <p className="text-xs text-muted-foreground">The owner sets pay and bank details.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => add.mutate()} disabled={!value.full_name.trim() || !value.join_date || add.isPending}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
